import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get token from localStorage
const getToken = () => localStorage.getItem('noor_albayan_token');

// Default axios instance. We do NOT enable `withCredentials` globally because
// some CDN/proxy layers (Cloudflare) rewrite the preflight CORS response to
// `Access-Control-Allow-Origin: *`, which Chrome rejects when credentials are
// requested — surfaced as "Network Error" in axios. Auth is carried via the
// `Authorization: Bearer …` header (Bearer token from localStorage); only the
// endpoints that genuinely depend on the httpOnly cookie (login, logout,
// refresh-token) opt-in to `withCredentials` per call.
const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Endpoints that must send/receive the auth cookie.
const COOKIE_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/logout',
  '/auth/refresh-token',
];

// Add auth header interceptor + opt-in cookie for the few endpoints that need it.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (COOKIE_PATHS.some((p) => (config.url || '').startsWith(p))) {
    config.withCredentials = true;
  }
  return config;
});

// Token refresh queue to prevent multiple simultaneous refresh requests
let refreshTokenPromise = null;

// Response interceptor for automatic token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors and skip refresh/login endpoints
    if (error.response?.status === 401 && originalRequest.url && 
        !originalRequest.url.includes('/auth/refresh') && 
        !originalRequest.url.includes('/auth/login') &&
        !originalRequest.url.includes('/auth/logout')) {
      
      try {
        // If a refresh is already in progress, wait for it
        if (!refreshTokenPromise) {
          refreshTokenPromise = (async () => {
            try {
              const response = await api.post('/auth/refresh', {}, { withCredentials: true });
              const { token } = response.data;
              localStorage.setItem('noor_albayan_token', token);
              return token;
            } catch (refreshError) {
              // Refresh failed — clear token and signal the app via a custom
              // event so React Router can navigate client-side without a full
              // page reload (which causes a flicker/reload loop in Capacitor).
              localStorage.removeItem('noor_albayan_token');
              window.dispatchEvent(new CustomEvent('noor:auth:session-expired'));
              throw refreshError;
            } finally {
              refreshTokenPromise = null;
            }
          })();
        }

        const newToken = await refreshTokenPromise;
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Quran API
export const quranApi = {
  getSurahs: () => api.get('/quran/surahs'),
  getSurah: (number) => api.get(`/quran/surahs/${number}`),
  getVerses: (surahNumber) => api.get(`/quran/surahs/${surahNumber}/verses`),
  getVerseDetail: (surahNumber, verseNumber) => api.get(`/quran/verses/${surahNumber}/${verseNumber}`),
  getVerseOfTheDay: () => api.get('/quran/verse-of-the-day'),
};

// Hadith API
export const hadithApi = {
  getCollections: () => api.get('/hadith/collections'),
  getCollection: (slug) => api.get(`/hadith/collections/${slug}`),
  getBooks: (slug) => api.get(`/hadith/collections/${slug}/books`),
  getBook: (slug, bookNumber) => api.get(`/hadith/collections/${slug}/books/${bookNumber}`),
  getEntries: (slug, bookNumber, { skip = 0, limit = 50 } = {}) =>
    api.get(`/hadith/collections/${slug}/books/${bookNumber}/entries`, { params: { skip, limit } }),
  getEntry: (entryId) => api.get(`/hadith/entries/${entryId}`),
  getSanad: (entryId) => api.get(`/hadith/entries/${entryId}/sanad`),
  getNarrator: (narratorId) => api.get(`/hadith/narrators/${narratorId}`),
  search: (q, { collection = null, skip = 0, limit = 20 } = {}) =>
    api.get('/hadith/search', { params: { q, collection, skip, limit } }),
};

// Prophet API
export const prophetApi = {
  getProphets: () => api.get('/prophets'),
  getProphet: (id) => api.get(`/prophets/${id}`),
};

// Companion API
export const companionApi = {
  getCompanions: (skip = 0, limit = 50, search = '') => 
    api.get('/companions', { params: { skip, limit, search: search || undefined } }),
  getCompanion: (id) => api.get(`/companions/${id}`),
};

// Event API
export const eventApi = {
  getEvents: (period = null, skip = 0, limit = 50) => 
    api.get('/events', { params: { period, skip, limit } }),
  getTimeline: () => api.get('/events/timeline'),
  getEvent: (id) => api.get(`/events/${id}`),
};

// Tafsir API
export const tafsirApi = {
  getSources: (language = null) =>
    api.get('/tafsir/sources', { params: { language } }),
  getByVerse: (surahNumber, verseNumber, { language = null, source = null } = {}) =>
    api.get(`/tafsir/verses/${surahNumber}/${verseNumber}`, {
      params: { language, source },
    }),
  search: (q, { language = null, source = null, limit = 10, skip = 0 } = {}) =>
    api.get('/tafsir/search', {
      params: { q, language, source, limit, skip },
    }),
};

// Search API
export const searchApi = {
  search: (query, types = null, limit = 10, skip = 0) => 
    api.get('/search/', { params: { q: query, types, limit, skip } }),
};

// Semantic Links API
export const linksApi = {
  // Generic: get all links (outgoing + reverse bidirectional) for an entity
  getLinks: (entityType, entityId, { targetType = null, relation = null, limit = 50, skip = 0 } = {}) =>
    api.get(`/links/for/${entityType}/${encodeURIComponent(entityId)}`, {
      params: { target_type: targetType, relation, limit, skip },
    }),
  resolve: (targetType, targetIds) =>
    api.get('/links/resolve', {
      params: { target_type: targetType, target_ids: targetIds.join(',') },
    }),
  stats: () => api.get('/links/stats'),
};

// User API
export const userApi = {
  getBookmarks: () => api.get('/user/bookmarks'),
  createBookmark: (data) => api.post('/user/bookmarks', data),
  deleteBookmark: (id) => api.delete(`/user/bookmarks/${id}`),
  getNotes: () => api.get('/user/notes'),
  createNote: (data) => api.post('/user/notes', data),
  updateNote: (id, data) => api.put(`/user/notes/${id}`, data),
  deleteNote: (id) => api.delete(`/user/notes/${id}`),
};

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  createSurah: (data) => api.post('/admin/surahs', data),
  createVerse: (data) => api.post('/admin/verses', data),
  createVerseContext: (data) => api.post('/admin/verse-contexts', data),
  createTafsir: (data) => api.post('/admin/tafsirs', data),
  createHadithCollection: (data) => api.post('/admin/hadith-collections', data),
  createHadith: (data) => api.post('/admin/hadiths', data),
  linkHadithVerse: (verseId, hadithId) =>
    api.post(`/admin/hadith-verse-links?verse_id=${verseId}&hadith_id=${hadithId}`),
  createProphet: (data) => api.post('/admin/prophets', data),
  updateProphet: (id, data) => api.put(`/admin/prophets/${id}`, data),
  deleteProphet: (id) => api.delete(`/admin/prophets/${id}`),
  createCompanion: (data) => api.post('/admin/companions', data),
  updateCompanion: (id, data) => api.put(`/admin/companions/${id}`, data),
  deleteCompanion: (id) => api.delete(`/admin/companions/${id}`),
  createEvent: (data) => api.post('/admin/events', data),
  updateEvent: (id, data) => api.put(`/admin/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/admin/events/${id}`),
  // Semantic-link review
  links: {
    list: (params = {}) => api.get('/admin/links', { params }),
    stats: () => api.get('/admin/links/stats'),
    patch: (id, data) => api.patch(`/admin/links/${id}`, data),
    create: (data) => api.post('/admin/links', data),
    remove: (id) => api.delete(`/admin/links/${id}`),
  },
  // Lexical glossary generation (admin → background job)
  lexical: {
    status: () => api.get('/admin/lexical/status'),
    generate: (data) => api.post('/admin/lexical/generate-glosses', data),
    fullSync: () => api.post('/admin/lexical/full-sync'),
  },
  // Morphology management (production tools)
  morphology: {
    status: () => api.get('/admin/morphology/status'),
    import: () => api.post('/admin/morphology/import'),
    rebuild: () => api.post('/admin/morphology/rebuild'),
    validate: () => api.post('/admin/morphology/validate'),
  },
  // Glosses generation (OpenAI definitions)
  glosses: {
    status: () => api.get('/admin/glosses/status'),
    generate: (mode = 'all', limit = null) => api.post(`/admin/glosses/generate?mode=${mode}${limit ? `&limit=${limit}` : ''}`),
    cancel: () => api.post('/admin/glosses/cancel'),
  },
};

// Memorization API
export const memorizationApi = {
  listPlans: () => api.get('/memorization/plans'),
  createPlan: (data) => api.post('/memorization/plans', data),
  getPlan: (id) => api.get(`/memorization/plans/${id}`),
  updatePlan: (id, data) => api.put(`/memorization/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/memorization/plans/${id}`),
  getExpectedWords: (id) => api.get(`/memorization/plans/${id}/expected`),
  submitValidation: (id, audioBlob, hintsUsed = 0) => {
    const fd = new FormData();
    fd.append('audio', audioBlob, 'recitation.webm');
    fd.append('hints_used', String(hintsUsed));
    return api.post(`/memorization/plans/${id}/validations`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  history: (limit = 30) => api.get('/memorization/history', { params: { limit } }),
  revisions: () => api.get('/memorization/revisions'),
  startSession: (planId) => api.post('/memorization/sessions', null, { params: { plan_id: planId } }),
  updateSession: (sessionId, data) => api.put(`/memorization/sessions/${sessionId}`, data),
};

// Quran Lexical Explorer API (read-only, public)
export const lexicalApi = {
  /** Fetch all word analyses for a surah (used to render words clickable). */
  getSurahWords: (surahNumber) =>
    api.get(`/lexical/surah/${surahNumber}/words`),
  /** Single token (all segments) at a given (ayah_key, word_index). */
  getWord: (ayahKey, wordIndex) =>
    api.get(`/lexical/word/${encodeURIComponent(ayahKey)}/${wordIndex}`),
  getLemma: (lemma, language = 'fr') =>
    api.get(`/quran/lemmas/${encodeURIComponent(lemma)}`, { params: { language } }),
  getLemmaOccurrences: (lemma, page = 1, limit = 50) =>
    api.get(`/quran/lemmas/${encodeURIComponent(lemma)}/occurrences`, { params: { page, limit } }),
  getRoot: (root, language = 'fr') =>
    api.get(`/quran/roots/${encodeURIComponent(root)}`, { params: { language } }),
  getRootOccurrences: (root, page = 1, limit = 50) =>
    api.get(`/quran/roots/${encodeURIComponent(root)}/occurrences`, { params: { page, limit } }),
  /** Multi-mode grouped search: exact_word | lemma | root | all. */
  search: (q, mode = 'all', page = 1, limit = 25) =>
    api.get('/quran/words/search', { params: { q, mode, page, limit } }),
  /** Autocomplete suggestions. */
  suggest: (q, limit = 10) =>
    api.get('/quran/lexical/suggest', { params: { q, limit } }),
  /** Glossary lookup with FR/EN/AR fallback. */
  getGloss: (type, value, language = 'fr') =>
    api.get(`/quran/lexical/gloss/${type}/${encodeURIComponent(value)}`, { params: { language } }),
  /** Words with optional gloss enrichment for a single ayah. */
  getAyahWords: (ayahKey, language = 'fr') =>
    api.get(`/quran/ayahs/${encodeURIComponent(ayahKey)}/words`, { params: { language } }),
  /** Tajwīd rule annotations for a single ayah. */
  getAyahTajwid: (ayahKey) =>
    api.get(`/quran/ayahs/${encodeURIComponent(ayahKey)}/tajwid`),
  /** Ops snapshot. */
  getDebugStatus: () =>
    api.get('/quran/lexical/debug/status'),
  getSurahSummary: (surahNumber) =>
    api.get(`/lexical/surah/${surahNumber}/lexical-summary`),
};

// Courses progress (reading + tajweed pedagogical journey)
export const coursesApi = {
  listExercises: ({ section = null, skill = null, skills = null, lessonId = null, lessonIds = null, ruleId = null, difficulty = null, limit = 200 } = {}) =>
    api.get('/courses/exercises', {
      params: { section, skill, skills, lessonId, lessonIds, ruleId, difficulty, limit },
    }),
  getExercise: (id) => api.get(`/courses/exercises/${id}`),
  getExerciseStatus: (ids) => api.get('/courses/exercise-status', {
    params: { ids: Array.isArray(ids) ? ids.join(',') : ids },
  }),
  logAttempt: (data) => api.post('/courses/attempts', data),
  logTajweedAttempt: (data) => api.post('/courses/tajweed-attempts', data),
  myProgress: () => api.get('/courses/progress/mine'),
  recommendations: () => api.get('/courses/progress/recommendations'),
  surahTajweedSummary: (surahNumber) =>
    api.get(`/courses/tajweed-summary/${surahNumber}`),
};

// Curriculum / Pedagogical journey
export const learningApi = {
  listCurriculums: (type = null) => api.get('/learning/curriculums', { params: { type } }),
  getCurriculum: (id) => api.get(`/learning/curriculums/${id}`),
  curriculumProgress: (id) => api.get(`/learning/curriculums/${id}/progress`),
  competency: (id) => api.get(`/learning/competencies/${id}`),
  competencyExercises: (id, difficulty = null) =>
    api.get(`/learning/competencies/${id}/exercises`, { params: { difficulty } }),
  progressSummary: () => api.get('/learning/progress/summary'),
  recommendations: () => api.get('/learning/recommendations'),
  startSeries: (data) => api.post('/learning/series/start', data),
  completeSeries: (id, data) => api.post(`/learning/series/${id}/complete`, data),
  // Assignments
  listAssignments: () => api.get('/learning/assignments'),
  getAssignment: (id) => api.get(`/learning/assignments/${id}`),
  createAssignment: (data) => api.post('/learning/assignments', data),
  startAssignment: (id) => api.post(`/learning/assignments/${id}/start`),
  submitAssignment: (id, data) => api.post(`/learning/assignments/${id}/submit`, data),
  previewAssignment: (constraints) => api.post('/learning/assignments/preview', constraints),
  // Badges & seen-tracking
  badges: () => api.get('/learning/assignments/badges'),
  markSeen: (id) => api.post(`/learning/assignments/${id}/mark-seen`),
  markSubmissionsSeen: (id) =>
    api.post(`/learning/assignments/${id}/submissions/mark-seen`),
};

// Tajwīd educational module
export const tajwidApi = {
  getRules: () => api.get('/tajwid/rules'),
  getLessons: () => api.get('/tajwid/lessons'),
  getLesson: (rule) => api.get(`/tajwid/lessons/${encodeURIComponent(rule)}`),
  getSurahExercise: (surahNumber) =>
    api.get(`/tajwid/surahs/${surahNumber}/exercise`),
};

// Madrassa module
export const madrassaApi = {
  // Public-ish (auth user)
  listActive: () => api.get('/madrassas/active'),
  listPartners: () => api.get('/madrassas/partners'),
  myMemberships: () => api.get('/madrassas/me'),
  myStatus: () => api.get('/madrassas/me/status'),
  myTeachingClasses: () => api.get('/madrassas/me/classes'),
  requestCreate: (data) => api.post('/madrassas/requests/create', data),
  requestJoin: (data) => api.post('/madrassas/requests/join', data),
  // Teacher onboarding aliases
  createTeacherRequest: (id, data = {}) =>
    api.post(`/madrassas/${id}/teacher-requests`, data),
  listTeacherRequests: (id) =>
    api.get(`/madrassas/${id}/teacher-requests`),
  acceptTeacherRequest: (id, rid, data = {}) =>
    api.post(`/madrassas/${id}/teacher-requests/${rid}/accept`, data),
  rejectTeacherRequest: (id, rid, data = {}) =>
    api.post(`/madrassas/${id}/teacher-requests/${rid}/reject`, data),
  // Single madrassa
  get: (id) => api.get(`/madrassas/${id}`),
  delete: (id, force = false) =>
    api.delete(`/madrassas/${id}`, { params: { force } }),
  listMembers: (id, status = null) =>
    api.get(`/madrassas/${id}/members`, { params: { status } }),
  decideMembership: (id, mid, data) =>
    api.post(`/madrassas/${id}/members/${mid}/decide`, data),
  invite: (id, data) => api.post(`/madrassas/${id}/members/invite`, data),
  removeMember: (id, mid) => api.delete(`/madrassas/${id}/members/${mid}`),
  // Classes
  listClasses: (id) => api.get(`/madrassas/${id}/classes`),
  classStats: (cid) => api.get(`/madrassas/classes/${cid}/stats`),
  createClass: (id, data) => api.post(`/madrassas/${id}/classes`, data),
  updateClass: (id, cid, data) => api.put(`/madrassas/${id}/classes/${cid}`, data),
  deleteClass: (id, cid) => api.delete(`/madrassas/${id}/classes/${cid}`),
  listClassMembers: (id, cid) =>
    api.get(`/madrassas/${id}/classes/${cid}/members`),
  addClassMember: (id, cid, data) =>
    api.post(`/madrassas/${id}/classes/${cid}/members`, data),
  removeClassMember: (id, cid, uid) =>
    api.delete(`/madrassas/${id}/classes/${cid}/members/${uid}`),
  // CSV import
  importPreview: (id, formData) =>
    api.post(`/madrassas/${id}/import/preview`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  importCommit: (id, formData) =>
    api.post(`/madrassas/${id}/import/commit`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Global admin
  adminList: (params = {}) => api.get('/madrassas/admin/all', { params }),
  adminCreate: (data) => api.post('/madrassas/admin/create', data),
  adminUpdate: (id, data) => api.patch(`/madrassas/admin/${id}`, data),
  adminApprove: (id) => api.post(`/madrassas/admin/${id}/approve`),
  adminReject: (id) => api.post(`/madrassas/admin/${id}/reject`),
  adminEmailLogs: () => api.get('/madrassas/admin/email-logs'),
};

// Lot 35 — Admin / Madrassa-admin / Teacher user management
export const adminUsersApi = {
  list: (params = {}) => api.get('/admin/users', { params }),
  detail: (id) => api.get(`/admin/users/${id}`),
  setStatus: (id, status) => api.patch(`/admin/users/${id}/status`, { status }),
};

export const teacherApi = {
  classes: () => api.get('/teacher/classes'),
  classDetail: (id) => api.get(`/teacher/classes/${id}`),
  classStudents: (id) => api.get(`/teacher/classes/${id}/students`),
  studentDetail: (id) => api.get(`/teacher/students/${id}`),
};

// Madrassa-scoped users views (admin / madrassa_admin)
madrassaApi.listUsers = (madrassaId, params = {}) =>
  api.get(`/madrassas/${madrassaId}/users`, { params });
madrassaApi.listTeachers = (madrassaId, params = {}) =>
  api.get(`/madrassas/${madrassaId}/teachers`, { params });
madrassaApi.listStudents = (madrassaId, params = {}) =>
  api.get(`/madrassas/${madrassaId}/students`, { params });
madrassaApi.userDetail = (madrassaId, userId) =>
  api.get(`/madrassas/${madrassaId}/users/${userId}`);

// ────────────────────────────────────────────────────────────────────
// Recitation API (Lot 42)
// ────────────────────────────────────────────────────────────────────
export const recitationApi = {
  upload: (assignmentId, body) =>
    api.post(`/recitations/assignments/${assignmentId}/upload`, body),
  listMine: (assignmentId) =>
    api.get(`/recitations/assignments/${assignmentId}/mine`),
  listForTeacher: (assignmentId) =>
    api.get(`/recitations/assignments/${assignmentId}/list`),
  audioUrl: (recId) => `${API_URL}/api/recitations/${recId}/audio`,
  grade: (recId, body) => api.post(`/recitations/${recId}/grade`, body),
};

// ────────────────────────────────────────────────────────────────────
// Prayer Times API
// ────────────────────────────────────────────────────────────────────
export const prayerApi = {
  // Get prayer times for a specific date and location
  getTimings: (latitude, longitude, timezone = 'Europe/Paris', method = 2, date = null) =>
    api.get('/prayer-times/timings', {
      params: { lat: latitude, lng: longitude, timezone, method, date }
    }),
  
  // Get full month's prayer times
  getMonthly: (latitude, longitude, timezone = 'Europe/Paris', method = 2, year = null, month = null) =>
    api.get('/prayer-times/monthly', {
      params: { lat: latitude, lng: longitude, timezone, method, year, month }
    }),
  
  // Geocode: address to coordinates
  geocode: (query, limit = 5) =>
    api.get('/prayer-times/geocode', { params: { q: query, limit } }),
  
  // Reverse geocode: coordinates to address
  reverseGeocode: (latitude, longitude) =>
    api.get('/prayer-times/reverse', { params: { lat: latitude, lng: longitude } }),
  
  // Get user's prayer time preferences (requires auth)
  getPreferences: () =>
    api.get('/me/prayer-preferences'),
  
  // Save user's prayer time preferences (requires auth)
  setPreferences: (preferences) =>
    api.put('/me/prayer-preferences', preferences),
};

// ────────────────────────────────────────────────────────────────────
// Donations API
// ────────────────────────────────────────────────────────────────────
export const donationsApi = {
  // Public
  listActiveQuests: (category) =>
    api.get('/donations/quests', { params: category ? { category } : {} }),
  getQuestBySlug: (slug) => api.get(`/donations/quests/${slug}`),
  start: (body) => api.post('/donations/start', body),
  myDonations: () => api.get('/donations/me'),
  // Admin
  admin: {
    list: (params = {}) => api.get('/admin/quests', { params }),
    create: (body) => api.post('/admin/quests', body),
    get: (id) => api.get(`/admin/quests/${id}`),
    update: (id, body) => api.patch(`/admin/quests/${id}`, body),
    remove: (id) => api.delete(`/admin/quests/${id}`),
    listDonations: (params = {}) => api.get('/admin/donations', { params }),
  },
};

export default api;
